import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column, type ColumnFilterElementTemplateOptions } from "primereact/column";
import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { FilterMatchMode } from "primereact/api";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { SplitButton } from "primereact/splitbutton";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { AdminAppRoutes, buildRoute } from "sj-ui-toolkit/src/constants/routes";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils/user";
import { humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import {
  DEFAULT_ROW_COUNT,
  ROWS_PER_PAGE_OPTIONS,
} from "sj-ui-toolkit/src/constants/tableRowsCounts";
import type { AssessmentData } from "@/api/assessments";
import { STATUS_CONFIG, type KaStatusKey } from "sj-ui-toolkit/src/constants/assessmentStatus";
import { STATUS_CODE_MAP } from "@/constants/assessmentsReport.constants";
import { skeletonRows, withSkeleton, statusBody } from "@/helpers/tableBody";
import { textFilterElement, multiSelectFilterElement } from "@/helpers/tableFilters";
import ArchiveAssessmentDialog from "./ArchiveAssessmentDialog";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StatusOption {
  code?: string;
  displayName?: string;
}

interface KnowledgeAssessmentsTableProps {
  assessments: AssessmentData[] | "loading";
  statusList: StatusOption[];
  onArchiveStateChange: () => void;
  viewOnly: boolean;
}

interface ArchiveTarget {
  targetId: string;
  targetName: string;
  isArchiving: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EMPTY_ROW_TEXT = "No assessments found";

const EMPTY_FILTERS = {
  global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  title: { value: null as string | null, matchMode: FilterMatchMode.STARTS_WITH },
  statusName: { value: null as string[] | null, matchMode: FilterMatchMode.IN },
  startDate: { value: null as Date[] | null, matchMode: FilterMatchMode.BETWEEN },
  endDate: { value: null as Date[] | null, matchMode: FilterMatchMode.BETWEEN },
};

// ---------------------------------------------------------------------------
// Column body templates — no runtime deps
// ---------------------------------------------------------------------------

const titleBody = withSkeleton<AssessmentData>(row => row.title ?? "—", "14rem");

const startDateBody = withSkeleton<AssessmentData>(
  row => (row.startDate ? humanDateWithoutTime(row.startDate) : "—"),
  "7rem"
);

const endDateBody = withSkeleton<AssessmentData>(
  row => (row.dynamic ? "Dynamic" : row.endDate ? humanDateWithoutTime(row.endDate) : "—"),
  "7rem"
);

const completeBody = withSkeleton<AssessmentData>(
  row => `${row.completedCount ?? 0}/${row.totalLearners ?? 0}`,
  "5rem"
);

const assessmentStatusBody = statusBody<AssessmentData>(row => {
  if (row.statusCode) {
    const statusKey = STATUS_CODE_MAP[row.statusCode] as KaStatusKey | undefined;
    const config = statusKey ? STATUS_CONFIG[statusKey] : { icon: "", color: "" };
    const colorClass = config.color === "warning" ? themeClasses.textWarning : config.color;
    return {
      icon: config.icon || undefined,
      colorClass: colorClass || undefined,
      label: row.statusName ?? row.statusCode ?? "—",
    };
  }
  return { label: row.statusName ?? "—" };
});

// ---------------------------------------------------------------------------
// Filter element helpers
// ---------------------------------------------------------------------------

const dateRangeFilterElement = (options: ColumnFilterElementTemplateOptions) => (
  <div className="mt-4">
    <Calendar
      value={(options.value as Date[] | null) ?? undefined}
      onChange={e => options.filterApplyCallback(e.value)}
      selectionMode="range"
      readOnlyInput
      hideOnRangeSelection
      placeholder="Select date range"
      className="w-full"
    />
  </div>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function KnowledgeAssessmentsTable({
  assessments,
  statusList,
  onArchiveStateChange,
  viewOnly,
}: KnowledgeAssessmentsTableProps) {
  const navigate = useNavigate();
  const dt = useRef<DataTable<DataTableValueArray>>(null);

  const isLoading = assessments === "loading";
  const stateKey = `knowledgeAssessmentsTable-${getCurrentSelectedTenant() ?? ""}`;

  const [filters, setFilters] = useState(() => {
    try {
      const stored = localStorage.getItem(stateKey);
      if (stored) {
        const parsed = JSON.parse(stored) as { filters?: typeof EMPTY_FILTERS };
        if (parsed.filters) return { ...EMPTY_FILTERS, ...parsed.filters };
      }
    } catch {
      // ignore corrupt storage
    }
    return { ...EMPTY_FILTERS };
  });
  const [globalFilterValue, setGlobalFilterValue] = useState(() => {
    try {
      const stored = localStorage.getItem(stateKey);
      if (stored) {
        const parsed = JSON.parse(stored) as { filters?: { global?: { value?: string | null } } };
        return parsed.filters?.global?.value ?? "";
      }
    } catch {
      // ignore corrupt storage
    }
    return "";
  });
  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);

  // Once statuses load, default filter to exclude "Archived"
  useEffect(() => {
    if (statusList.length === 0) return;
    const names = statusList
      .map(s => s.displayName)
      .filter((n): n is string => !!n)
      .filter(n => n !== "Archived");
    setFilters(prev => ({
      ...prev,
      statusName: { value: names, matchMode: FilterMatchMode.IN },
    }));
  }, [statusList]);

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, global: { ...prev.global, value } }));
    setGlobalFilterValue(value);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setGlobalFilterValue("");
  };

  // Status filter element — recreated when statusList changes
  const statusFilterElement = useMemo(
    () =>
      multiSelectFilterElement(
        statusList
          .filter(s => !!s.displayName)
          .map(s => ({ label: s.displayName!, value: s.displayName! })),
        "Select status"
      ),
    [statusList]
  );

  // Read persisted row count once on mount so skeleton and paginator agree
  const storedRows = useMemo(() => {
    try {
      const stored = localStorage.getItem(stateKey);
      if (stored) {
        const parsed: { rows?: number } = JSON.parse(stored);
        if (typeof parsed.rows === "number") return parsed.rows;
      }
    } catch {
      // ignore
    }
    return DEFAULT_ROW_COUNT;
  }, []);

  const skeletonItems = useMemo(() => skeletonRows<AssessmentData>(storedRows), [storedRows]);

  // ---------------------------------------------------------------------------
  // Column body — runtime deps
  // ---------------------------------------------------------------------------

  const actionsBody = useMemo(
    () =>
      withSkeleton<AssessmentData>(row => {
        const menuItems = [
          {
            label: "Edit",
            icon: "pi pi-pencil",
            disabled: viewOnly,
            command: () => {
              navigate(
                buildRoute(AdminAppRoutes.routes.assessments.routes.id.route, {
                  id: row.uuid ?? "",
                })
              );
            },
          },
          {
            label: row.archivedDate ? "Unarchive" : "Archive",
            icon: "pi pi-warehouse",
            disabled: viewOnly,
            command: () => {
              setArchiveTarget({
                targetId: row.uuid ?? "",
                targetName: row.title ?? "",
                isArchiving: !row.archivedDate,
              });
            },
          },
        ];
        return (
          <SplitButton
            label="View Results"
            icon="pi pi-chart-line"
            model={menuItems}
            onClick={() =>
              navigate(
                buildRoute(AdminAppRoutes.routes.reporting.routes.assessmentId.route, {
                  id: row.uuid ?? "",
                })
              )
            }
            outlined
            menuButtonProps={{ "data-testid": `assessment-row-${row.uuid ?? ""}-menu-button` }}
          />
        );
      }, "10rem"),
    [navigate, viewOnly]
  );

  // ---------------------------------------------------------------------------
  // Header
  // ---------------------------------------------------------------------------

  const renderHeader = () => (
    <div className="flex gap-4">
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder="Keyword Search"
          autoComplete="off"
        />
      </IconField>
      <Button
        label="Clear"
        icon="pi pi-filter-slash"
        size="small"
        severity="secondary"
        className={globalFilterValue ? "" : "hidden"}
        onClick={clearFilters}
      />
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      {archiveTarget && (
        <ArchiveAssessmentDialog
          visible
          targetId={archiveTarget.targetId}
          targetName={archiveTarget.targetName}
          isArchiving={archiveTarget.isArchiving}
          onComplete={completed => {
            setArchiveTarget(null);
            if (completed) onArchiveStateChange();
          }}
        />
      )}

      <Card className={themeClasses.bgElevation1}>
        <div className="mb-4">{renderHeader()}</div>
        <DataTable
          ref={dt}
          aria-label="Knowledge assessments"
          value={(isLoading ? skeletonItems : assessments) as unknown as DataTableValueArray}
          stateKey={stateKey}
          stateStorage="local"
          paginator
          filters={isLoading ? undefined : filters}
          onFilter={e => setFilters(e.filters as typeof filters)}
          filterDisplay="menu"
          size="normal"
          globalFilterFields={isLoading ? [] : ["title", "statusName"]}
          rows={storedRows}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          sortField="title"
          sortOrder={1}
          removableSort
          emptyMessage={EMPTY_ROW_TEXT}
          tableClassName="dt-responsive-text"
        >
          <Column
            field="title"
            header="Assessment"
            sortable
            filter
            showFilterMatchModes={false}
            filterElement={textFilterElement("Search...")}
            body={titleBody}
            style={{ width: "350px" }}
          />
          <Column
            field="statusName"
            header="Status"
            sortable
            filter
            filterElement={statusFilterElement}
            showFilterMatchModes={false}
            body={assessmentStatusBody}
          />
          <Column
            field="startDate"
            header="Start date"
            sortable
            filter
            dataType="date"
            showFilterMatchModes={false}
            showApplyButton={false}
            showClearButton={false}
            filterElement={dateRangeFilterElement}
            body={startDateBody}
            style={{ width: "180px" }}
          />
          <Column
            field="endDate"
            header="End date"
            sortable
            filter
            dataType="date"
            showFilterMatchModes={false}
            showApplyButton={false}
            showClearButton={false}
            filterElement={dateRangeFilterElement}
            body={endDateBody}
            style={{ width: "180px" }}
          />
          <Column
            field="completedCount"
            header="Complete"
            sortable
            body={completeBody}
            style={{ width: "120px" }}
          />
          <Column header="" body={actionsBody} style={{ width: "240px" }} />
        </DataTable>
      </Card>
    </>
  );
}
